import { createTranslator } from "use-intl";

import { buildSeoMetaTags } from "@/lib/seo-meta";
import { buildWebsiteJsonLd } from "@/utils/buildWebsiteJsonLd";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

interface BuildRootHeadInput {
  locale: ViewerLocale;
  messages: Record<string, any>;
}

export function buildRootHead({ locale, messages }: BuildRootHeadInput) {
  const t = createTranslator({ locale, messages, namespace: "globals.metadata" });
  const title = t("title");
  const description = t("description");

  return {
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, viewport-fit=cover",
      },
      { name: "theme-color", content: "#121212" },
      ...buildSeoMetaTags(
        { title, description },
        {
          openGraph: {
            type: "website",
            siteName: "Garimpa.Club",
          },
          twitter: {
            card: "summary_large_image",
            includeTitle: false,
            includeDescription: false,
          },
        },
      ),
    ],
    links: [
      {
        rel: "icon",
        href: "/favicon.ico",
        type: "image/vnd.microsoft.icon",
        sizes: "16x16 32x32 48x48 256x256",
      },
      { rel: "icon", href: "/icons/icon-192.png", type: "image/png", sizes: "192x192" },
      { rel: "icon", href: "/icon.svg", type: "image/svg+xml", sizes: "any" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png", sizes: "180x180" },
      {
        rel: "preload",
        href: "/fonts/space-grotesk/space-grotesk-latin-wght-normal.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous" as const,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildWebsiteJsonLd()),
      },
    ],
  };
}
